import analyticsService from '@lib/analytics/AnalyticsService'
import { useEffectAsync } from '@lib/hooks/useEffectAsync'
import { useRouter } from 'next/router'
import type { NextRouter } from 'next/router'
import { useRef } from 'react'

/**
 * Converts router query params to a record of strings.
 * Handles array values by joining them with commas.
 */
const queryToRecord = (query: NextRouter['query']): Record<string, string> => {
    const record: Record<string, string> = {}
    Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined) {
            record[key] = Array.isArray(value) ? value.join(',') : value
        }
    })
    return record
}

/**
 * Converts router query params to URLSearchParams entries.
 */
const queryToEntries = (
    query: NextRouter['query'],
): Array<[string, string]> => {
    const entries: Array<[string, string]> = []
    Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined) {
            const stringValue = Array.isArray(value) ? value.join(',') : value
            entries.push([key, stringValue])
        }
    })
    return entries
}

/**
 * Initializes analytics service and tracks page views on route changes.
 *
 * Features:
 * - Initializes analytics service once with query params
 * - Tracks page views on route changes
 * - Prevents duplicate tracking for the same path
 */
export const useAnalytics = () => {
    const router = useRouter()
    const lastTrackedPath = useRef<string | null>(null)

    // Initialize analytics service
    useEffectAsync(async () => {
        const queryEntries = queryToEntries(router.query)
        const searchParams = new URLSearchParams(queryEntries)
        await analyticsService.initialize(searchParams)
    }, [])

    // Track page views
    useEffectAsync(async () => {
        const pathName = router.asPath
        if (!pathName) return

        // Skip if it's the same path we already tracked
        if (pathName === lastTrackedPath.current) return

        const queryParams = queryToRecord(router.query)

        await analyticsService.trackPageView({
            pathname: pathName,
            queryParams,
            fullUrl: pathName,
        })
        lastTrackedPath.current = pathName
    }, [router.asPath])
}
